import React, { useState } from 'react';
import {
  Box,
  Badge,
  Button,
  Divider,
  Flex,
  HStack,
  Skeleton,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiFetchNotifications, apiMarkNotificationRead } from 'api';
import Card from 'components/card/Card';
import { NotificationDetailModal } from 'components/notifications/NotificationDetailModal';

interface FeedNotification {
  id: number;
  title: string;
  summary: string;
  content?: string;
  link_url?: string;
  status?: string;
  is_edited?: boolean;
  requires_acknowledgement?: boolean;
  created_at?: string;
  attachments?: any[];
}

export const ImportantUpdatesFeed: React.FC = () => {
  const navigate    = useNavigate();
  const bgColor     = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('#CBD5E0', 'rgba(135,140,189,0.5)');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<FeedNotification | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['homepage-notifications'],
    queryFn: () =>
      apiFetchNotifications(1, undefined, 5).then((r: any) =>
        Array.isArray(r) ? r : (r?.results ?? [])
      ),
    staleTime: 2 * 60 * 1000,
  });

  const formatDate = (dateString: string) => {
    const diff  = Date.now() - new Date(dateString).getTime();
    const days  = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    if (days  > 0) return `${days} day${days  > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => apiMarkNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['homepage-notifications'] }),
  });

  const notifications: FeedNotification[] = data ?? [];

  const handleClick = (n: FeedNotification) => {
    if (n.status === 'unread') markRead.mutate(n.id);
    // All notifications open the detail modal — mandatory ones show
    // the Acknowledge button inside the modal itself.
    setSelected(n);
    onOpen();
  };

  return (
    <>
      <Card
        bg={bgColor}
        borderRadius="16px"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        p="0"
      >
        {/* Header */}
        <Box px="20px" pt="20px" pb="16px" borderBottom="1px solid" borderColor={borderColor}>
          <Text fontSize="xl" fontWeight="800" color={textColor} lineHeight="1.2">
            Recent Updates
          </Text>
        </Box>

        {/* Body */}
        <Box flex="1" overflowY="auto" px="20px">
          {isLoading ? (
            <VStack spacing="0" align="stretch" py="12px">
              {[1, 2, 3].map((i) => (
                <Box key={i} py="14px" borderBottom="1px solid" borderColor={borderColor}>
                  <Skeleton height="11px" w="60%" mb="8px" borderRadius="4px" />
                  <Skeleton height="10px" mb="4px" borderRadius="4px" />
                  <Skeleton height="10px" w="80%" borderRadius="4px" />
                </Box>
              ))}
            </VStack>
          ) : notifications.length === 0 ? (
            <Flex h="120px" alignItems="center" justifyContent="center">
              <Text fontSize="sm" color={mutedColor}>
                No updates at this time
              </Text>
            </Flex>
          ) : (
            <VStack spacing="0" align="stretch">
              {notifications.map((n, idx) => {
                const isUnread      = n.status === 'unread';
                const formattedDate = n.created_at ? formatDate(n.created_at) : '';
                return (
                  <Box
                    key={n.id}
                    py="12px"
                    borderBottom={idx < notifications.length - 1 ? '1px solid' : 'none'}
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={() => handleClick(n)}
                    _hover={{ bg: 'blackAlpha.50' }}
                    transition="background 0.15s"
                    borderRadius="md"
                  >
                    <HStack justify="space-between" align="flex-start" mb="4px">
                      <Text
                        fontSize="sm"
                        fontWeight={isUnread ? '700' : '600'}
                        color={isUnread ? textColor : mutedColor}
                        flex={1}
                        mr={2}
                        noOfLines={1}
                      >
                        {n.title}
                      </Text>
                      <HStack flexShrink={0} spacing={1}>
                        {n.is_edited && (
                          <Badge colorScheme="blue" fontSize="10px">EDITED</Badge>
                        )}
                        {n.requires_acknowledgement && (
                          <Badge colorScheme="red" fontSize="10px">⚠ MANDATORY</Badge>
                        )}
                      </HStack>
                    </HStack>
                    {n.summary && (
                      <Text fontSize="xs" color={mutedColor} noOfLines={2} mb="4px">
                        {n.summary}
                      </Text>
                    )}
                    {formattedDate && (
                      <Text fontSize="xs" color={mutedColor}>
                        {formattedDate}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>

        {/* Footer — only shown when there is at least one notification */}
        {!isLoading && notifications.length > 0 && (
        <Box flexShrink={0}>
          <Divider borderColor={borderColor} />
          <Box px="20px" py="16px">
          <Button
            w="100%"
            bg="#f97316"
            color="white"
            borderRadius="10px"
            fontWeight="700"
            fontSize="sm"
            _hover={{ bg: '#ea6c0a' }}
            _active={{ bg: '#d96009' }}
            onClick={() => navigate('/admin/notifications')}
          >
            View All
          </Button>
          </Box>
        </Box>
        )}
      </Card>

      {/* Inline detail modal — same pop-up pattern as the full notifications page */}
      <NotificationDetailModal
        isOpen={isOpen}
        onClose={onClose}
        notification={selected as any}
      />
    </>
  );
};
